import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Permission, CourseDTO, Res } from 'core/api';
import { deepEquals } from 'core/utils';
import * as SalesStore from 'pos/modules/main/SalesStore';
import { Command } from 'pos/common';
import { Cols, Col } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';
import { ActionButtonDefinition, PosStatelessScreenClassComponent, ScreenSize } from 'pos/ui';

const formatQuantity = UI.Format.asDecimalWith(2);


function Course({ course }: { course: CourseDTO }) {

	const addins = course.Addins.map((addin, index) =>
		<span key={index}><br />{Res.Web.TICKET_LINES_CONTROL_PRODUCT_WITH_LBL()} {addin} </span>);

	const notes = course.Notes.map((note, index) =>
		<span key={index}><br />*  {note}  *</span>);
	const saleFormatName = <span>{course.Product}</span>;

	return <li>
		<Cols>
			<Col width='6ex' className='text-right'>
				{formatQuantity(course.Quantity)}
			</Col>
			<Col width='*' className='m-l-xs'>
				{saleFormatName}
				{addins}
				{notes}
			</Col>
		</Cols>
	</li>;
}

function Header({ preparationOrder, totalPreparationOrderQuantity }: { preparationOrder: string, totalPreparationOrderQuantity: number }) {
	return <Cols className='text-center list-header'>
		<Col width='*'>{preparationOrder} ({totalPreparationOrderQuantity})</Col>
	</Cols>;
}

type Props = { ticketIds: number[], courses: CourseDTO[] } & Modal.Actions<void>;

class ShowCourses extends PosStatelessScreenClassComponent<Props> {

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	protected getScreenId(): string {
		return 'SHOW_COURSES';
	}

	protected getTitle(): string {
		return Res.Web.SHOW_COURSES();
	}

	protected getImage(): string | SvgIcon {
		return Images.GetCoursesToPrepare;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected useKeypad() {
		return false;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			{
				title: Res.Web.PREPARE_TICKET_BTN(), image: Images.PrepareTicket, action: async () => {
					await Command.execute('prepareMultipleTickets', this.props.ticketIds);
					this.close();
				}
			}
		];
	}

	protected getContent(): React.ReactNode {
		const groups = this.props.courses.groupBy(x => x.PreparationOrder, x => x, (x, y) => deepEquals(x, y)).map((group, idx) => {
			return <React.Fragment key={idx}>
				<Header
					preparationOrder={group.key.Name}
					totalPreparationOrderQuantity={group.value.sum(x => x.Quantity)} />
				<ul className='list list-multiple'>
					{group.value.map((course, idx) => <Course key={idx} course={course} />)}
				</ul>
			</React.Fragment>;
		});

		return <>
			{groups}
		</>;
	}
}

declare global {
	interface CommandType {
		showCourses: (ticketIds: number[]) => Promise<Result>
	}
}

Command.register('showCourses', Preconditions.ensure(Permission.Ticket_PrepareTicket), async (ticketIds: number[]): Promise<Result> => {
	const courses = await SalesStore.getCoursesToPrepare(ticketIds);

	if (!courses.length) {
		await Message.show(Res.Msg.MSG_THERE_ARE_NOT_COURSES_TO_PREPARE());
		return Result.error();
	}

	return Modal.show(ShowCourses, { courses: courses, ticketIds: ticketIds });
});